import type { IBaseCampaignVersion } from '~/modules/campaign';

import {
  useGetQueryInValues,
  useGetQueryValues,
} from '~/presentation/reports/query/hooks/useGetQueryValues';
import { EFilterField } from '~/presentation/reports/common/type';
import { useCampaignMaps } from '~/modules/campaign-template';
import { convertToDateTimeFilters } from '~/presentation/ui';
import { getVersionsByCampaignGroup } from '../utils';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export const useDateTime = () => {
  const { t } = useTranslation();
  const { campaignMap, campaignGroupMap } = useCampaignMaps();

  const [campaignId, campaignGroupId] = useGetQueryValues({
    fields: [EFilterField.SINGLE_CAMPAIGN, EFilterField.GROUP_CAMPAIGN],
  });
  const [listCampaignIds] = useGetQueryInValues({
    fields: [EFilterField.SINGLE_CAMPAIGN],
  });

  const campaignIdsInGroup = useMemo(() => {
    if (!campaignGroupId) return [];

    const results = [...(listCampaignIds ?? [])];
    if (campaignId) results.push(campaignId);

    return results;
  }, [listCampaignIds, campaignId, campaignGroupId]);

  const campaign = useMemo(() => {
    if (!campaignId) return undefined;

    return campaignMap[campaignId];
  }, [campaignMap, campaignId]);

  const additionalRangesByCampaignGroup = useMemo(() => {
    const versions = getVersionsByCampaignGroup({
      campaignIds: campaignIdsInGroup,
      campaignGroupId,
      campaignGroupMap,
    });

    return convertToDateTimeFilters(versions);
  }, [campaignGroupMap, campaignIdsInGroup, campaignGroupId]);

  const additionalRangesByCampaign = useMemo(() => {
    if (!campaign?.versions) return [];

    return convertToDateTimeFilters(campaign.versions as IBaseCampaignVersion[]);
  }, [campaign]);

  const additionalRanges = useMemo(() => {
    if (campaignGroupId) return additionalRangesByCampaignGroup;

    return additionalRangesByCampaign;
  }, [additionalRangesByCampaignGroup, additionalRangesByCampaign, campaignGroupId]);

  const rangeAddition = useMemo(() => {
    if (!additionalRanges.length) return undefined;
    return {
      title: t('Experiment periods'),
      rangers: additionalRanges,
    };
  }, [additionalRanges, t]);

  return {
    initDate: additionalRanges?.[0],
    rangeAddition,
  };
};
